import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const CartContext = createContext(null);
const STORAGE_KEY = 'alp_cart_v1';

export const STANDARD_SHIPPING = 13;
export const FREE_EXPRESS_THRESHOLD = 300;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const addToCart = (product, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.id === product.id ? { ...i, qty: i.qty + qty } : i
        );
      }
      return [
        ...prev,
        {
          id: product.id,
          name: product.name,
          code: product.code,
          size: product.size,
          price: product.price,
          image: product.image,
          stockStatus: product.stockStatus,
          digital: product.digital || false,
          giftCard: product.giftCard || null,
          qty,
        },
      ];
    });
  };

  const updateQty = (id, qty) => {
    if (qty <= 0) return removeFromCart(id);
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, qty } : i)));
  };

  const removeFromCart = (id) =>
    setItems((prev) => prev.filter((i) => i.id !== id));

  const clearCart = () => setItems([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const openDrawer = () => setDrawerOpen(true);
  const closeDrawer = () => setDrawerOpen(false);

  const cartCount = useMemo(
    () => items.reduce((sum, i) => sum + i.qty, 0),
    [items]
  );

  const subtotal = useMemo(
    () => items.reduce((sum, i) => sum + i.price * i.qty, 0),
    [items]
  );

  const hasPreorder = useMemo(
    () => items.some((i) => i.stockStatus === 'Preorder'),
    [items]
  );
  const hasInStock = useMemo(
    () => items.some((i) => i.stockStatus === 'In Stock'),
    [items]
  );
  const hasPhysical = useMemo(
    () => items.some((i) => !i.digital && !i.giftCard),
    [items]
  );

  const inStockSubtotal = useMemo(
    () =>
      items
        .filter((i) => i.stockStatus === 'In Stock')
        .reduce((sum, i) => sum + i.price * i.qty, 0),
    [items]
  );

  const freeExpress = hasInStock && inStockSubtotal >= FREE_EXPRESS_THRESHOLD;
  const shipping = items.length === 0 || freeExpress || !hasPhysical ? 0 : STANDARD_SHIPPING;
  const total = subtotal + shipping;

  const value = {
    items,
    addToCart,
    updateQty,
    removeFromCart,
    clearCart,
    cartCount,
    subtotal,
    shipping,
    total,
    hasPreorder,
    hasInStock,
    hasPhysical,
    freeExpress,
    drawerOpen,
    openDrawer,
    closeDrawer,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
